import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrgStore } from '../../stores/orgStore';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Table } from '../../components/ui/Table/Table';
import { Badge } from '../../components/ui/Badge/Badge';
import { Button } from '../../components/ui/Button/Button';
import { canvasService } from '../../services/canvasService';

interface CanvasRow {
  id: string;
  name: string;
  node_count: number;
  creator_name: string | null;
  created_at: string;
  updated_at: string;
}

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function OrgCanvases() {
  const activeOrg = useOrgStore(s => s.orgs.find(org => org.id === s.activeOrgId));
  const [canvases, setCanvases] = useState<CanvasRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleDuplicate = async (canvasId: string) => {
    setDuplicatingId(canvasId);
    try {
      const { data } = await canvasService.duplicate(canvasId);
      navigate(`/canvas/${data.canvas.id}`);
    } catch (err) {
      console.error('Failed to duplicate canvas:', err);
      setDuplicatingId(null);
    }
  };

  useEffect(() => {
    if (!activeOrg?.id) return;
    setLoading(true);
    canvasService.list()
      .then((res: any) => {
        setCanvases(res.data || []);
      })
      .catch((err: any) => {
        console.error('Failed to load canvases:', err);
      })
      .finally(() => setLoading(false));
  }, [activeOrg?.id]);

  const totalNodes = canvases.reduce((sum, c) => sum + (c.node_count || 0), 0);

  const columns = [
    {
      key: 'name',
      title: 'Name',
      sortable: true,
      render: (_: any, record: CanvasRow) => (
        <div className="font-medium text-gray-900">{record.name}</div>
      )
    },
    {
      key: 'node_count',
      title: 'Nodes',
      sortable: true,
      render: (value: number) => (
        <Badge variant={value > 0 ? 'default' : 'outline'}>{value}</Badge>
      )
    },
    {
      key: 'creator_name',
      title: 'Created by',
      sortable: true,
      render: (value: string | null) => (
        <span className="text-sm text-gray-600">{value || 'Unknown'}</span>
      )
    },
    {
      key: 'created_at',
      title: 'Created',
      sortable: true,
      render: (value: string) => (
        <span className="text-sm text-gray-500">{formatDate(value)}</span>
      )
    },
    {
      key: 'updated_at',
      title: 'Last edited',
      sortable: true,
      render: (value: string) => (
        <span className="text-sm text-gray-500">{formatRelativeDate(value)}</span>
      )
    },
    {
      key: 'actions',
      title: '',
      align: 'right' as const,
      render: (_: any, record: CanvasRow) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDuplicate(record.id)}
            disabled={duplicatingId === record.id}
          >
            {duplicatingId === record.id ? 'Duplicating...' : 'Duplicate'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/canvas/${record.id}`)}
          >
            View
          </Button>
        </div>
      )
    }
  ];

  if (!activeOrg) {
    return (
      <PageContainer>
        <div className="text-center text-sm text-muted-foreground">No organization selected</div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Canvases"
        description="View and manage all canvases in your organization"
      />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-sm text-gray-500">Total canvases</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{canvases.length}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-sm text-gray-500">Total nodes</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{totalNodes}</div>
        </div>
      </div>

      {/* Table */}
      <Table
        data={canvases}
        columns={columns}
        loading={loading}
        searchable={true}
        searchPlaceholder="Search canvases..."
        emptyText="No canvases yet. Create one from the dashboard."
        pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: [5, 10, 20] }}
      />
    </PageContainer>
  );
}
